import React, { useMemo, useState } from "react";
import { Card, Row, Col, Typography, Tooltip, Select } from "antd";
import {
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    ResponsiveContainer, Tooltip as RTooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
    ComposedChart, Line,
} from "recharts";

const { Text, Title } = Typography;

interface DimensionScore {
    calls_scored: number;
    calls_skipped: number;
    score: number | null;
    weight: string;
}

interface AgentProfile {
    agent: string;
    overall_score: number;
    grade: string;
    label: string;
    total_calls_analyzed: number;
    dimension_scores: Record<string, DimensionScore>;
    metadata: {
        priority_distribution: Record<string, number>;
        resolution_distribution: Record<string, number>;
        scoring_note: string;
    };
}

interface Props {
    agentProfiles: AgentProfile[];
}

const AGENT_COLORS = [
    "#1677ff", "#52c41a", "#fa8c16", "#f5222d",
    "#722ed1", "#13c2c2", "#eb2f96", "#fadb14",
    "#2f54eb",
];

const DIMENSION_SHORT: Record<string, string> = {
    professionalism: "Prof.",
    issue_resolution: "Resolution",
    customer_satisfaction: "Satisfaction",
    call_closure_quality: "Closure",
    empathy_soft_skills: "Empathy",
    knowledge_competency: "Knowledge",
};

const GRADE_COLOR: Record<string, string> = {
    A: "#52c41a", B: "#1677ff", C: "#faad14", D: "#fa8c16", F: "#ff4d4f",
};

const scoreColor = (v: number) =>
    v >= 60 ? "#52c41a" : v >= 40 ? "#faad14" : "#ff4d4f";

/* ─────────────────────────────────────────────
   1. QA Score Leaderboard  (Horizontal ComposedChart)
───────────────────────────────────────────── */
const ScoreLeaderboard = ({ profiles }: { profiles: AgentProfile[] }) => {
    const sorted = useMemo(
        () => [...profiles].sort((a, b) => b.overall_score - a.overall_score),
        [profiles]
    );

    const data = sorted.map((p, i) => ({
        name: p.agent.split(" ")[0],
        fullName: p.agent,
        score: parseFloat(p.overall_score.toFixed(1)),
        grade: p.grade,
        color: AGENT_COLORS[i % AGENT_COLORS.length],
    }));

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🏆</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>QA Score Leaderboard</span>
                    <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 4 }}>
                        Ranked by overall performance
                    </Text>
                </div>
            }
            bordered={false}
            className="shadow-sm rounded-xl"
            bodyStyle={{ padding: "12px 16px" }}
        >
            <ResponsiveContainer width="100%" height={Math.max(220, data.length * 42)}>
                <ComposedChart
                    layout="vertical"
                    data={data}
                    margin={{ top: 4, right: 60, left: 0, bottom: 4 }}
                >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} tickCount={6} />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={70}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v, i) => data[i]?.fullName || v}
                    />
                    <RTooltip
                        formatter={(val: any, _: any, props: any) => [
                            `${val} / 100`,
                            `${props.payload?.fullName} (${props.payload?.grade})`,
                        ]}
                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    {/* 60-mark reference line */}
                    <Line
                        type="monotone"
                        dataKey={() => 60}
                        stroke="#7c3aed"
                        strokeDasharray="4 3"
                        strokeWidth={1.5}
                        dot={false}
                        name="Threshold (60)"
                    />
                    <Bar dataKey="score" radius={[0, 5, 5, 0]} maxBarSize={22} label={{
                        position: "right",
                        formatter: (v: any) => `${v}`,
                        fontSize: 11,
                        fill: "#595959",
                    }}>
                        {data.map((d, i) => (
                            <Cell key={i} fill={scoreColor(d.score)} fillOpacity={0.85} />
                        ))}
                    </Bar>
                </ComposedChart>
            </ResponsiveContainer>
            {/* grade legend */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, justifyContent: "center" }}>
                {sorted.map((p, i) => (
                    <Tooltip key={p.agent} title={`${p.label} · ${p.total_calls_analyzed} calls analyzed`}>
                        <span style={{
                            fontSize: 11, padding: "2px 10px", borderRadius: 999, cursor: "default",
                            background: `${GRADE_COLOR[p.grade]}18`,
                            color: GRADE_COLOR[p.grade],
                            border: `1px solid ${GRADE_COLOR[p.grade]}55`,
                            fontWeight: 600,
                        }}>
                            {p.agent.split(" ")[0]} — {p.grade}
                        </span>
                    </Tooltip>
                ))}
            </div>
        </Card>
    );
};

/* ─────────────────────────────────────────────
   2. Radar Chart — Dimension comparison (pick up to 4 agents)
───────────────────────────────────────────── */
const DimensionRadar = ({ profiles }: { profiles: AgentProfile[] }) => {
    const allNames = profiles.map(p => p.agent);
    const [selected, setSelected] = useState<string[]>(allNames.slice(0, 4));

    const chosen = profiles.filter(p => selected.includes(p.agent));

    const dimensions = Object.keys(profiles[0]?.dimension_scores ?? {});

    const radarData = useMemo(() =>
        dimensions.map(dim => {
            const row: Record<string, any> = { dim: DIMENSION_SHORT[dim] ?? dim };
            chosen.forEach(p => {
                row[p.agent] = p.dimension_scores[dim]?.score ?? 0;
            });
            return row;
        }),
        [chosen, dimensions]
    );

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 16 }}>🕸️</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Dimension Radar</span>
                    <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
                        Compare skill dimensions across agents
                    </Text>
                </div>
            }
            extra={
                <Select
                    mode="multiple"
                    size="small"
                    style={{ minWidth: 180, maxWidth: 260 }}
                    value={selected}
                    onChange={v => setSelected(v.slice(0, 4))}
                    options={allNames.map(n => ({ label: n.split(" ")[0], value: n }))}
                    maxTagCount={2}
                    placeholder="Select agents"
                />
            }
            bordered={false}
            className="shadow-sm rounded-xl"
            bodyStyle={{ padding: "12px 16px" }}
        >
            {chosen.length === 0
                ? <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Text type="secondary">Select at least one agent</Text>
                </div>
                : <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarData} margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
                        <PolarGrid stroke="#e5e7eb" />
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        {React.createElement(PolarAngleAxis as any, { dataKey: "dim", tick: { fontSize: 11, fill: "#595959" } })}
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: "#bfbfbf" }} />
                        <RTooltip
                            formatter={(val: any, name: any) => [`${Number(val).toFixed(1)}`, String(name).split(" ")[0]] as any}
                            contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v.split(" ")[0]} />
                        {chosen.map((p, i) => (
                            <Radar
                                key={p.agent}
                                name={p.agent}
                                dataKey={p.agent}
                                stroke={AGENT_COLORS[i % AGENT_COLORS.length]}
                                fill={AGENT_COLORS[i % AGENT_COLORS.length]}
                                fillOpacity={0.12}
                                strokeWidth={2}
                            />
                        ))}
                    </RadarChart>
                </ResponsiveContainer>
            }
        </Card>
    );
};

/* ─────────────────────────────────────────────
   3. Resolution Distribution — stacked bar per agent
───────────────────────────────────────────── */
const ResolutionChart = ({ profiles }: { profiles: AgentProfile[] }) => {
    const data = useMemo(() =>
        [...profiles]
            .sort((a, b) => b.overall_score - a.overall_score)
            .map(p => ({
                name: p.agent.split(" ")[0],
                fullName: p.agent,
                Yes: p.metadata?.resolution_distribution?.Yes ?? 0,
                Partially: p.metadata?.resolution_distribution?.Partially ?? 0,
                No: p.metadata?.resolution_distribution?.No ?? 0,
                total: p.total_calls_analyzed,
            })),
        [profiles]
    );

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>📊</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Resolution Breakdown</span>
                    <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 4 }}>
                        Yes / Partially / No per agent
                    </Text>
                </div>
            }
            bordered={false}
            className="shadow-sm rounded-xl"
            bodyStyle={{ padding: "12px 16px" }}
        >
            <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data} barCategoryGap="30%" margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v, i) => data[i]?.fullName?.split(" ")[0] ?? v}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        labelFormatter={(_, p) => p?.[0]?.payload?.fullName ?? ""}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Yes" stackId="a" fill="#52c41a" fillOpacity={0.85} radius={[0, 0, 0, 0]} name="Resolved" />
                    <Bar dataKey="Partially" stackId="a" fill="#faad14" fillOpacity={0.85} name="Partial" />
                    <Bar dataKey="No" stackId="a" fill="#ff4d4f" fillOpacity={0.85} radius={[4, 4, 0, 0]} name="Unresolved" />
                </BarChart>
            </ResponsiveContainer>
        </Card>
    );
};

/* ─────────────────────────────────────────────
   4. Dimension Score Heatmap (table-style)
───────────────────────────────────────────── */
const DimensionHeatmap = ({ profiles }: { profiles: AgentProfile[] }) => {
    const dimensions = Object.keys(profiles[0]?.dimension_scores ?? {});
    const sorted = [...profiles].sort((a, b) => b.overall_score - a.overall_score);

    const cellBg = (score: number | null) => {
        if (score === null) return "#f5f5f5";
        if (score >= 60) return `rgba(82,196,26,${0.12 + (score / 100) * 0.35})`;
        if (score >= 40) return `rgba(250,173,20,${0.15 + (score / 100) * 0.3})`;
        return `rgba(255,77,79,${0.12 + ((100 - score) / 100) * 0.3})`;
    };

    const cellText = (score: number | null) => {
        if (score === null) return "#bfbfbf";
        if (score >= 60) return "#389e0d";
        if (score >= 40) return "#d48806";
        return "#cf1322";
    };

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🔥</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Dimension Score Heatmap</span>
                    <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 4 }}>
                        All agents × all scoring dimensions
                    </Text>
                </div>
            }
            bordered={false}
            className="shadow-sm rounded-xl"
            bodyStyle={{ padding: "12px 16px", overflowX: "auto" }}
        >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: "left", padding: "6px 10px", color: "#8c8c8c", fontWeight: 600, whiteSpace: "nowrap", borderBottom: "1px solid #f0f0f0" }}>
                            Agent
                        </th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: "#8c8c8c", fontWeight: 600, borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                            Score
                        </th>
                        {dimensions.map(d => (
                            <th key={d} style={{ textAlign: "center", padding: "6px 8px", color: "#8c8c8c", fontWeight: 600, borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                                {DIMENSION_SHORT[d] ?? d}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((p, i) => (
                        <tr key={p.agent} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff" }}>
                            <td style={{ padding: "7px 10px", fontWeight: 600, whiteSpace: "nowrap", color: "#262626" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{
                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                        width: 20, height: 20, borderRadius: "50%", fontSize: 10, fontWeight: 700,
                                        background: AGENT_COLORS[i % AGENT_COLORS.length],
                                        color: "#fff", flexShrink: 0,
                                    }}>
                                        {p.agent.charAt(0)}
                                    </span>
                                    {p.agent}
                                </div>
                            </td>
                            <td style={{ textAlign: "center", padding: "7px 8px" }}>
                                <span style={{
                                    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                                    background: cellBg(p.overall_score), color: cellText(p.overall_score),
                                }}>
                                    {p.overall_score.toFixed(1)}
                                </span>
                            </td>
                            {dimensions.map(d => {
                                const s = p.dimension_scores[d]?.score ?? null;
                                return (
                                    <td key={d} style={{ textAlign: "center", padding: "7px 8px" }}>
                                        <Tooltip title={s === null
                                            ? `Skipped (${p.dimension_scores[d]?.calls_skipped} calls)`
                                            : `${s.toFixed(1)} — weight ${p.dimension_scores[d]?.weight}`
                                        }>
                                            <span style={{
                                                display: "inline-block",
                                                minWidth: 38, padding: "3px 6px",
                                                borderRadius: 6,
                                                fontSize: 11, fontWeight: 600,
                                                background: cellBg(s),
                                                color: cellText(s),
                                                cursor: "default",
                                            }}>
                                                {s !== null ? s.toFixed(1) : "—"}
                                            </span>
                                        </Tooltip>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* color legend */}
            <div style={{ display: "flex", gap: 16, marginTop: 12, justifyContent: "flex-end" }}>
                {[
                    { label: "Good (≥60)", color: "rgba(82,196,26,0.35)", text: "#389e0d" },
                    { label: "Average (40-60)", color: "rgba(250,173,20,0.35)", text: "#d48806" },
                    { label: "Poor (<40)", color: "rgba(255,77,79,0.35)", text: "#cf1322" },
                    { label: "N/A", color: "#f5f5f5", text: "#bfbfbf" },
                ].map(l => (
                    <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 14, height: 14, borderRadius: 3, background: l.color, display: "inline-block" }} />
                        <Text style={{ fontSize: 10, color: l.text }}>{l.label}</Text>
                    </div>
                ))}
            </div>
        </Card>
    );
};

/* ─────────────────────────────────────────────
   5. Priority Distribution — grouped bar per agent
───────────────────────────────────────────── */
const PriorityChart = ({ profiles }: { profiles: AgentProfile[] }) => {
    const data = useMemo(() =>
        [...profiles]
            .sort((a, b) => b.overall_score - a.overall_score)
            .map(p => ({
                name: p.agent.split(" ")[0],
                fullName: p.agent,
                High: p.metadata?.priority_distribution?.High ?? 0,
                Medium: p.metadata?.priority_distribution?.Medium ?? 0,
                Low: p.metadata?.priority_distribution?.Low ?? 0,
            })),
        [profiles]
    );

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>⚡</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Call Priority Mix</span>
                    <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 4 }}>
                        High / Medium / Low priority calls per agent
                    </Text>
                </div>
            }
            bordered={false}
            className="shadow-sm rounded-xl"
            bodyStyle={{ padding: "12px 16px" }}
        >
            <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data} barCategoryGap="30%" barGap={3} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        labelFormatter={(_, p) => p?.[0]?.payload?.fullName ?? ""}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="High" fill="#ff4d4f" fillOpacity={0.8} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Medium" fill="#faad14" fillOpacity={0.8} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Low" fill="#52c41a" fillOpacity={0.8} radius={[3, 3, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </Card>
    );
};

/* ─────────────────────────────────────────────
   Main export
───────────────────────────────────────────── */
const AgentProfileCharts = ({ agentProfiles }: Props) => {
    if (!agentProfiles || agentProfiles.length === 0) return null;

    return (
        <div style={{ marginTop: 8 }}>
            {/* Section header */}
            <div style={{
                display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
                paddingBottom: 10, borderBottom: "1px solid #f0f0f0",
            }}>
                <div style={{
                    width: 32, height: 32, background: "linear-gradient(135deg,#7c3aed,#1677ff)",
                    borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                }}>
                    <span style={{ fontSize: 16 }}>📈</span>
                </div>
                <div>
                    <Title level={5} style={{ margin: 0, letterSpacing: "-0.3px" }}>QA Performance Analytics</Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Multi-dimensional analysis of agent scoring data
                    </Text>
                </div>
            </div>

            <div className="space-y-4">
                {/* Row 1: Leaderboard + Radar side-by-side */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <ScoreLeaderboard profiles={agentProfiles} />
                    </Col>
                    <Col xs={24} lg={12}>
                        <DimensionRadar profiles={agentProfiles} />
                    </Col>
                </Row>

                {/* Row 2: Resolution + Priority side-by-side */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <ResolutionChart profiles={agentProfiles} />
                    </Col>
                    <Col xs={24} lg={12}>
                        <PriorityChart profiles={agentProfiles} />
                    </Col>
                </Row>

                {/* Row 3: Full-width heatmap */}
                <DimensionHeatmap profiles={agentProfiles} />
            </div>
        </div>
    );
};

export default AgentProfileCharts;
